import React, { useEffect, useRef, useCallback, useState } from 'react';
import Hls from 'hls.js';
import './VideoV2.css';

const HLS_URL = process.env.REACT_APP_API_VIDEO_HLS_URL || '/hls/index.m3u8';
const CONFIG_URL = process.env.REACT_APP_API_VIDEO_CONFIG_URL;
const ROTATION_KEY = 'video_rotation_v2';
const DEFAULT_ROTATION = 90;

const HLS_CONFIG = {
    maxBufferLength: 30,
    maxMaxBufferLength: 120,
    backBufferLength: 60,
    fragLoadingMaxRetry: 6,
    fragLoadingRetryDelay: 1000,
    manifestLoadingMaxRetry: 6,
    manifestLoadingRetryDelay: 1000,
    lowLatencyMode: false,
    enableWorker: true,
};

function getContainerStyle(rotation) {
    const needsSwap = rotation === 90 || rotation === -90;
    return {
        position: 'fixed',
        backgroundColor: '#000',
        overflow: 'hidden',
        transformOrigin: 'center center',
        transform: `rotate(${rotation}deg)`,
        width: needsSwap ? '100vh' : '100vw',
        height: needsSwap ? '100vw' : '100vh',
        top: needsSwap ? 'calc(50vh - 50vw)' : 0,
        left: needsSwap ? 'calc(50vw - 50vh)' : 0,
    };
}

function readLocalRotation() {
    const stored = localStorage.getItem(ROTATION_KEY);
    return stored !== null ? parseInt(stored, 10) : DEFAULT_ROTATION;
}

const VideoV2 = () => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const onEndedRef = useRef(null);
    const lastHlsLengthRef = useRef(null);
    const [rotation, setRotation] = useState(readLocalRotation);

    const startHls = useCallback((version) => {
        const video = videoRef.current;
        if (!video) return;

        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        if (onEndedRef.current) {
            video.removeEventListener('ended', onEndedRef.current);
            onEndedRef.current = null;
        }

        const src = `${HLS_URL}?v=${version}`;

        if (Hls.isSupported()) {
            const hls = new Hls(HLS_CONFIG);
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);

            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data.fatal) {
                    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
                    else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
                    else hls.destroy();
                }
            });

            const onEnded = () => {
                video.currentTime = 0;
                video.play().catch(() => {});
            };
            onEndedRef.current = onEnded;
            video.addEventListener('ended', onEnded);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari/TV con soporte HLS nativo
            video.src = src;
        }
    }, []);

    const fetchConfig = useCallback(async () => {
        if (!CONFIG_URL) return;
        try {
            const configRes = await fetch(CONFIG_URL, { cache: 'no-store' });
            if (configRes.ok) {
                const config = await configRes.json();
                if (typeof config.rotationV2 === 'number') {
                    setRotation(prev => {
                        if (prev !== config.rotationV2) {
                            localStorage.setItem(ROTATION_KEY, config.rotationV2.toString());
                            return config.rotationV2;
                        }
                        return prev;
                    });
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        startHls(Date.now());
        fetchConfig(); // rotación correcta inmediatamente al montar

        const poll = setInterval(() => {
            // HLS y config son independientes: si uno falla no bloquea al otro
            (async () => {
                try {
                    const hlsRes = await fetch(HLS_URL, { method: 'HEAD', cache: 'no-store' });
                    const len = hlsRes.headers.get('content-length');
                    if (lastHlsLengthRef.current !== null && len !== lastHlsLengthRef.current) {
                        startHls(Date.now());
                    }
                    lastHlsLengthRef.current = len;
                } catch {}
            })();
            fetchConfig();
        }, 10000);

        // Storage event: recarga inmediata para mismo navegador
        const onStorage = (e) => {
            if (e.key === ROTATION_KEY && e.newValue !== null) {
                setRotation(parseInt(e.newValue, 10));
            }
            if (e.key === 'video_reload') {
                startHls(e.newValue || Date.now());
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            clearInterval(poll);
            window.removeEventListener('storage', onStorage);
            if (hlsRef.current) hlsRef.current.destroy();
            if (onEndedRef.current && videoRef.current) {
                videoRef.current.removeEventListener('ended', onEndedRef.current);
            }
        };
    }, [startHls, fetchConfig]);

    return (
        <div className="video-v2-container" style={getContainerStyle(rotation)}>
            <video
                ref={videoRef}
                className="video-v2-player"
                autoPlay
                muted
                playsInline
                preload="auto"
                controls={false}
            />
        </div>
    );
};

export default VideoV2;
